import { memo, useEffect, useRef, useState } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";
import { ScrollArea } from "@synthesis/shared-ui";

import { NotificationItem } from "./notification-item";
import { NOTIFICATION_ITEM_ESTIMATED_HEIGHT, NOTIFICATION_LIST_SKELETON_COUNT } from "../notifications-panel.lib";
import { useApplicationNotifications } from "../use-application-notifications";

export const NotificationsList = memo(() => {
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, notifications } = useApplicationNotifications();
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: notifications.length,
    estimateSize: () => NOTIFICATION_ITEM_ESTIMATED_HEIGHT,
    getScrollElement: () => parentRef.current,
    overscan: 6,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVirtualIndex = virtualItems.at(-1)?.index;
  const notificationsCount = notifications.length;

  useEffect(() => {
    if (lastVirtualIndex == null || !hasNextPage || isFetchingNextPage) {
      return;
    }

    if (lastVirtualIndex >= notificationsCount - 1) {
      fetchNextPage();
    }
  }, [lastVirtualIndex, hasNextPage, isFetchingNextPage, notificationsCount, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: NOTIFICATION_LIST_SKELETON_COUNT }).map((_, index) => (
          <div className="skeleton h-28 w-full rounded-[14px]" key={index} />
        ))}
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="border-neutrals-90 bg-neutrals-white flex min-h-[12rem] items-center justify-center rounded-xl border border-dashed p-6">
        <p className="paragraph-base text-type-minor text-center">Уведомлений нет</p>
      </div>
    );
  }

  return (
    <ScrollArea ref={parentRef} direction="vertical" rootClassName="min-h-0 flex-1" className="min-h-0 pr-3">
      <div className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
        {virtualItems.map(({ index, key, start }) => {
          const notification = notifications[index];

          if (!notification) {
            return null;
          }

          return (
            <div
              className="absolute left-0 top-0 w-full pb-3"
              data-index={index}
              key={key}
              ref={rowVirtualizer.measureElement}
              style={{
                transform: `translateY(${start}px)`,
                zIndex: hoveredItemIndex === index ? 10 : "auto",
              }}
              onMouseEnter={() => setHoveredItemIndex(index)}
              onMouseLeave={() => setHoveredItemIndex(null)}
            >
              <NotificationItem notification={notification} />
            </div>
          );
        })}
      </div>

      {isFetchingNextPage && <div className="skeleton h-28 w-full rounded-[14px]" />}
    </ScrollArea>
  );
});

NotificationsList.displayName = "NotificationsList";
